import { Alert, Typography } from '@mui/joy';
import { Warning } from '@phosphor-icons/react';
import { UseFormReset } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';

import { useAuthUser } from 'auth/hooks';
import { Form, FormField } from 'core/components';
import { InputType } from 'core/enums';
import { useFormContext } from 'core/states';
import { objectMapToSelectOptions } from 'core/utils';
import { DEFAULT_SHIPPING_METHOD_NAME } from 'corporate/constants';
import {
  CorporateLocationFormField,
  FreightTermFormField,
  ShippingMethodFormField,
} from 'corporate/elements';
import { CorporateLocationType } from 'corporate/enums';
import { EditShipToAddressButton } from 'quotes/components/CustomerQuoteDetails/Tabs/CustomerQuoteBaseInformation';
import { ShipSelectionDateFields } from 'quotes/components/CustomerQuoteUpsert/ShipSelectionDateFields';
import { ICustomerQuote, IProjectSubmitWarning } from 'quotes/types';
import { getOrderTypeLabels } from 'quotes/utils';
import { Color, Level, Size, Variant } from 'theme/enums';

import { submitOrderBlockMessageKeyMap } from './constants';
import { ISubmitOrderForm } from './types';
import { getSubmitOrderBlockReason } from './utils';

interface IProps {
  quote: ICustomerQuote;
  projectSubmitWarning?: IProjectSubmitWarning | null;
  isOpen?: boolean;
  onClose?: (reset?: UseFormReset<ISubmitOrderForm>) => void;
  onSubmit?: (reset?: UseFormReset<ISubmitOrderForm>) => (data: ISubmitOrderForm) => void;
  isSubmitOrderLoading?: boolean;
  disableAutoClose?: boolean;
  displayEditShipToAddressButton?: boolean;
  onSubmitSuccessEditShipToAddress?: () => void;
  isAlternateVersionEnabled?: boolean;
}

export const SubmitCustomerOrderForm = ({
  quote,
  projectSubmitWarning = null,
  isOpen,
  onClose,
  onSubmit,
  isSubmitOrderLoading = false,
  disableAutoClose = false,
  displayEditShipToAddressButton = false,
  onSubmitSuccessEditShipToAddress = null,
  isAlternateVersionEnabled = false,
}: IProps) => {
  const { t } = useTranslation();

  const { defaultValues } = useFormContext();
  const { user } = useAuthUser();

  const displayExternalUserIdField =
    !user.alternateVersionEnabled || (user.alternateVersionEnabled && user.companyAdmin);

  // The quote prop follows SSE updates while the drawer is open: once the order is being
  // processed or already has an external number, the drawer's own Submit must not fire.
  const submitBlockReason = getSubmitOrderBlockReason(quote);
  const isLocked = isSubmitOrderLoading || !!submitBlockReason;

  return (
    <Form
      title={t('titleSubmitOrder')}
      subtitle={t('messageConfirmSubmitOrder')}
      isOpen={isOpen}
      onClose={onClose}
      submitBtnLabel={t('buttonLabelSubmitOrder')}
      cancelBtnLabel={t('buttonLabelCancel')}
      onSubmit={onSubmit}
      disabledSubmit={isLocked}
      disabledCancel={isSubmitOrderLoading}
      disableAutoClose={disableAutoClose}
    >
      {submitBlockReason && (
        <Alert
          startDecorator={<Warning style={{ width: 24, height: 24 }} />}
          variant={Variant.SOFT}
          color={Color.WARNING}
          size={Size.LG}
          sx={{ alignItems: 'flex-start', width: '100%' }}
        >
          <Typography level={Level.BODY_SM}>
            <Trans
              i18nKey={submitOrderBlockMessageKeyMap[submitBlockReason]}
              values={{ externalOrderNumber: quote.externalOrderNumber }}
            />
          </Typography>
        </Alert>
      )}

      {/* Informational only: the BE lets this submit through, so it never feeds isLocked. */}
      {projectSubmitWarning?.message && (
        <Alert
          role="alert"
          startDecorator={<Warning style={{ width: 24, height: 24 }} />}
          variant={Variant.SOFT}
          color={Color.WARNING}
          size={Size.LG}
          sx={{ alignItems: 'flex-start', width: '100%' }}
        >
          <Typography level={Level.BODY_SM}>{projectSubmitWarning.message}</Typography>
        </Alert>
      )}

      {displayEditShipToAddressButton && (
        <EditShipToAddressButton
          quote={quote}
          text={t('buttonLabelClickEditShipToAddress')}
          onSubmitSuccess={onSubmitSuccessEditShipToAddress}
        />
      )}

      {displayExternalUserIdField && (
        <FormField
          name="externalUserId"
          type={InputType.TEXT_INPUT}
          inputProps={{
            label: t('labelExternalUserId'),
            required: true,
            disabled: isLocked,
          }}
        />
      )}

      <CorporateLocationFormField
        name="location"
        label={t('labelOrderLocation')}
        isAlternateVersionEnabled={isAlternateVersionEnabled}
        type={CorporateLocationType.SALES}
        disabled={isLocked}
        required
      />

      {isAlternateVersionEnabled && (
        <>
          <FormField
            name="orderType"
            type={InputType.SELECT}
            inputProps={{
              label: t('labelOrderType'),
              required: true,
              disabled: isLocked,
              options: objectMapToSelectOptions(getOrderTypeLabels()),
            }}
          />

          <ShipSelectionDateFields required disabled={isLocked} seedNoDeliveryDateSubmissionDay />

          <ShippingMethodFormField
            name="shippingMethod"
            defaultName={DEFAULT_SHIPPING_METHOD_NAME}
            initialValue={(defaultValues as ISubmitOrderForm)?.shippingMethod}
            disabled={isLocked}
            required
          />

          <FreightTermFormField name="freightTerm" disabled={isLocked} required={true} />
        </>
      )}
    </Form>
  );
};
